import React, { useContext } from 'react'
import { Box } from '@chakra-ui/react'
import {
  propConverter,
  PriceInfoContainer,
  PriceLabel,
  PriceLabelTooltip,
  Price,
  PriceAdditionalInfo,
  formatPrice,
  EcochequeBanner,
  SustainabilityBanner,
  PromoLabel,
  BPlusLabel,
  PROMO_LABEL_CONSTANTS,
  Link,
  Flex,
  Grid,
  RepairabilityIndex,
  EnergyClassLetter,
  MD,
  Popover,
  isMobile
} from 'chemistry'
import { getPriceProps } from '../../../common/price'
import { ProductSchema } from './SchemaOrg/ProductSchema'
import { getProductDescription, getProductGtin, getProductAvailability } from '../../../common/selectors/product'
import { isPraxis, isNL, replaceUnitsInTranslation, isNullOrUndefined, isBrico } from 'common/utils'
import { createLogger } from 'common/log'
import { TranslateContext } from 'client/common/translator'

const logger = createLogger('productDetailsFragmentInfo/ProductPrice')

export const getProductInformationSheetDocument = ({ documents, format, lang }) => {
  if (!Array.isArray(documents)) return
  const productInformationSheetDocuments = documents.filter(({ subType }) => subType === 'EPREL_PRODUCT_SHEET')
  if (productInformationSheetDocuments.length === 0) return
  if (isPraxis(format)) return productInformationSheetDocuments[0]
  if (isNL(lang)) return productInformationSheetDocuments.find(({ locale }) => locale === 'nl_BE')
  return productInformationSheetDocuments.find(({ locale }) => locale === 'fr_BE')
}

export const getEnergyLabelDocument = ({ documents = [], mediaAssets = [], images = [] }) => {
  let energyLabelDocument

  energyLabelDocument = [...mediaAssets, ...images].find(({ subType }) => subType === 'ENERGY_LABEL')
  if (energyLabelDocument)
    return {
      url: energyLabelDocument.url,
      isImage: true
    }

  energyLabelDocument = documents.find(({ subType }) => subType === 'ENERGY_LABEL')
  if (energyLabelDocument)
    return {
      url: energyLabelDocument.url,
      isImage: false // PDF
    }
}

const ProductInformationSheetLink = ({ href, t }) => (
  <Link variant='iconLink' href={href} target='_blank' rel='noreferrer' aria-label='PDF Doc'>
    {t('documents.product_information_sheet')}
  </Link>
)

const priceGridTemplateAreasMd = `
"price technicalDetails"
"productInformationSheet productInformationSheet"
`
const priceGridTemplateAreasSm = `
"price"
"technicalDetails"
"productInformationSheet"
`

const ProductPrice = props => {
  const t = useContext(TranslateContext)
  const { product, format, lang, isGuest, currentLocation, showPraxisPlusHint, name, host, productId, hideVLP } = props
  const { price, priceProps } = getPriceProps(product, lang, format, isGuest, currentLocation, showPraxisPlusHint, t)
  const {
    labelText,
    percentage,
    oldUnitPrice,
    oldPrice,
    unitPrice,
    packetText,
    showPriceLabel,
    showBPlusLabel,
    units,
    showPriceLabelInfo,
    labelHintText,
    unitsInPacket,
    isEcocheque,
    isSustainability,
    isVLP,
    ecochequeText,
    sustainabilityText,
    errors
  } = propConverter({ ...priceProps, isFeatured: true })

  const showPrice = props.showPrice
  if (errors) {
    logger.error(`PRICE_ERROR, product ${format} ${productId} has corrupted price on pdp`)
  }
  let crossedPrice = oldUnitPrice || oldPrice
  let bigPrice = unitPrice || price || crossedPrice
  let priceAdditionalInfo = `${packetText} `
  let unitsStr = oldUnitPrice || unitPrice ? replaceUnitsInTranslation(units) : null
  let additionalInfoPrice = unitsStr ? price || oldPrice : null
  const productInformationSheetDocument = getProductInformationSheetDocument({
    documents: product?.documents,
    format,
    lang
  })
  const { repairabilityIndex, energyRating, energyRatingType } = product?.technicalDetails || {}
  const showRepairabilityScore = isBrico(format) && !isNullOrUndefined(repairabilityIndex)
  const showEnergyClassLetter = !isNullOrUndefined(energyRating)
  const energyLabelDocument =
    showEnergyClassLetter &&
    getEnergyLabelDocument({
      documents: product?.documents,
      mediaAssets: product?.mediaAssets,
      images: product?.images
    })

  if (product?.packagePriceFirst) {
    crossedPrice = oldPrice
    bigPrice = price || crossedPrice
    priceAdditionalInfo = `${replaceUnitsInTranslation(units)} `
    unitsStr = null
    additionalInfoPrice = unitPrice || oldUnitPrice
  }
  return (
    <Box>
      <ProductSchema
        {...{
          ...product,
          name,
          image: product?.mainImage,
          url: `https://${host}${currentLocation}`,
          sku: productId,
          description: getProductDescription(product),
          gtin13: getProductGtin(product),
          availability: getProductAvailability(product?.itemAvailability),
          fullPriceProps: { price: bigPrice, unitPrice, unitsInPacket }
        }}
      />
      {showPrice && (
        <PriceInfoContainer>
          <PriceInfoContainer.Before>
            {showBPlusLabel && (
              <>
                <BPlusLabel text={labelText} />
                <PriceLabelTooltip
                  labelHintText={labelHintText}
                  price={price}
                  percentage={percentage}
                  format={format}
                  oldPrice={oldPrice}
                  isDetail
                />
              </>
            )}
            {isVLP && !showPriceLabel && !hideVLP && (
              <PromoLabel
                variant={PROMO_LABEL_CONSTANTS.VARIANT.alwaysLowPrice}
                format={format}
                lang={lang}
                mb='12px'
              />
            )}
            {showPriceLabel && (
              <PriceLabel text={labelText} percentage={percentage} isDetail>
                {showPriceLabelInfo && (
                  <PriceLabelTooltip
                    labelHintText={labelHintText}
                    price={price}
                    percentage={percentage}
                    format={format}
                    oldPrice={oldPrice}
                    isDetail
                  />
                )}
              </PriceLabel>
            )}
          </PriceInfoContainer.Before>
          <Grid
            order={50}
            sx={{
              gridTemplateAreas: priceGridTemplateAreasSm,
              gridAutoColumns: 'min-content',
              [MD]: {
                gridTemplateAreas: priceGridTemplateAreasMd
              }
            }}
          >
            <Box gridArea='price'>
              <Price oldPrice={crossedPrice} price={bigPrice} units={unitsStr}>
                {unitPrice && (
                  <PriceAdditionalInfo text={priceAdditionalInfo}>
                    <b>{formatPrice(additionalInfoPrice)}</b>
                  </PriceAdditionalInfo>
                )}
              </Price>
            </Box>
            {(showRepairabilityScore || showEnergyClassLetter) && (
              <Flex gridArea='technicalDetails' sx={{ ml: 0, mt: '8px', [MD]: { ml: '24px', mt: 0 } }} gap='4px'>
                {showRepairabilityScore && (
                  <RepairabilityIndex size='lg' index={repairabilityIndex} label={t('repairability_index.label')} />
                )}
                {showEnergyClassLetter && !energyLabelDocument?.url && (
                  <EnergyClassLetter letter={energyRating} energyClass={energyRatingType} />
                )}
                {showEnergyClassLetter && energyLabelDocument?.url && (
                  <Popover
                    trigger={<EnergyClassLetter letter={energyRating} energyClass={energyRatingType} />}
                    bodyProps={{
                      // uncomment when more product will have energy label PDF
                      // text:
                      //   !energyLabelDocument?.url &&
                      //   'Deze informatie is op dit moment nog niet door de leverancier aangeleverd.',
                      img: {
                        isImage: energyLabelDocument?.isImage,
                        src: energyLabelDocument?.url,
                        alt: 'Energy class'
                      }
                    }}
                    closeButtonProps={{ text: t('cross_icon.label') }}
                    placement={isMobile() ? 'bottom' : 'right'}
                  />
                )}
              </Flex>
            )}
            {productInformationSheetDocument && (
              <Box gridArea='productInformationSheet' mt='8px'>
                <ProductInformationSheetLink href={productInformationSheetDocument.url} t={t} />
              </Box>
            )}
          </Grid>
          <PriceInfoContainer.After>
            {isEcocheque && <EcochequeBanner {...{ ecochequeText }} />}
            {isSustainability && <SustainabilityBanner {...{ sustainabilityText }} />}
          </PriceInfoContainer.After>
        </PriceInfoContainer>
      )}
    </Box>
  )
}

export default ProductPrice
